import React from 'react';
import styles from './TreemapTags.module.scss';

interface TreemapTag {
  category: string;
  color: string;
  count: number;
  description?: string;
}

interface TreemapTagsProps {
  tags: TreemapTag[];
  selectedCategory: string | null;
  onTagClick: (category: string) => void;
}

const TreemapTags: React.FC<TreemapTagsProps> = ({ tags, selectedCategory, onTagClick }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = React.useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  // 点击容器外关闭 tooltip（移动端）
  React.useEffect(() => {
    if (!tooltip) return;
    const handleOutsideClick = () => setTooltip(null);
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [tooltip]);

  if (tags.length < 10) return null;

  // 边界修正：确保 tooltip 不溢出容器
  const clampPosition = (x: number, y: number, containerWidth: number, containerHeight: number) => {
    const tooltipWidth = 280;
    const tooltipHeight = 120;

    if (x + tooltipWidth > containerWidth) x = containerWidth - tooltipWidth - 10;
    if (y + tooltipHeight > containerHeight) y = y - tooltipHeight - 30;
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    return { x, y };
  };

  // PC 端：hover 跟随鼠标
  const handleMouseMove = (e: React.MouseEvent, description: string) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    let x = e.clientX - rect.left + 15;
    let y = e.clientY - rect.top + 15;

    const pos = clampPosition(x, y, rect.width, rect.height);
    setTooltip({ text: description, x: pos.x, y: pos.y });
  };

  const handleMouseLeave = () => setTooltip(null);

  // 移动端：点击显示在色块下方居中
  const handleClick = (e: React.MouseEvent, tag: TreemapTag) => {
    e.stopPropagation();
    onTagClick(tag.category);

    if (!tag.description || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const btnRect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    let x = btnRect.left - rect.left + btnRect.width / 2 - 140;
    let y = btnRect.bottom - rect.top + 10;

    const pos = clampPosition(x, y, rect.width, rect.height);

    // 再次点击同一个则关闭
    if (tooltip?.text === tag.description) {
      setTooltip(null);
    } else {
      setTooltip({ text: tag.description, x: pos.x, y: pos.y });
    }
  };

  const getFontSize = (index: number): string => {
    // [最小px, 理想vw, 最大px]
    const sizes: [number, number, number][] = [
      [16, 2.5, 36],   // Modernization
      [13, 1.8, 26],   // High-quality development
      [11, 1.4, 20],   // Economic and social development
      [11, 1.4, 20],   // 15th Five-Year Plan
      [11, 1.4, 20],   // Economic work
      [10, 1.2, 18],   // Party self-governance
      [10, 1.2, 18],   // Sci-tech innovation 
      [8, 1.1, 15],    // New quality productive forces
      [10, 1.0, 14],   // Fighting corruption
      [10, 1.0, 14],   // Urban work
    ];
    const s = sizes[index] || [9, 1.1, 14];
    return `clamp(${s[0]}px, ${s[1]}vw, ${s[2]}px)`;
  };

  const columns = [
    tags.slice(0, 2),
    tags.slice(2, 5),
    tags.slice(5, 8),
    tags.slice(8, 10),
  ];

  const columnClasses = [styles.col1, styles.col2, styles.col3, styles.col4];

  return (
    <div ref={containerRef} className={styles.treemapContainer}>
      {columns.map((columnTags, colIndex) => (
        <div key={colIndex} className={`${styles.column} ${columnClasses[colIndex]}`}>
          {columnTags.map((tag, tagIndex) => {
            const globalIndex = colIndex === 0 ? tagIndex : 
                               colIndex === 1 ? tagIndex + 2 :
                               colIndex === 2 ? tagIndex + 5 : tagIndex + 8;
            const isSelected = selectedCategory === tag.category;
            const fontSize = getFontSize(globalIndex);

            return (
              <button
                key={tag.category}
                className={`${styles.block} ${isSelected ? styles.selected : ''}`}
                style={{ backgroundColor: tag.color }}
                onClick={(e) => handleClick(e, tag)}
                onMouseMove={(e) => tag.description && handleMouseMove(e, tag.description)}
                onMouseLeave={handleMouseLeave}
              >
                <span 
                  className={styles.blockLabel}
                  style={{ fontSize }}
                >
                  {tag.category}
                  <span className={styles.blockValue}>{tag.count}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}

      {tooltip && (
        <div 
          className={styles.tooltip}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default TreemapTags;
